import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration } from 'chart.js';
import { Subscription } from 'rxjs';
import { SpendIntelligenceService } from 'src/app/Services/spend-intelligence.service';
import { ChartPalette, ChartPaletteService, withAlpha } from 'src/app/Services/chart-palette.service';
import { AbbreviatedNumberPipe } from 'src/app/Shared/Pipes/abbreviated-number.pipe';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SkTableLoaderComponent } from 'src/app/CommonComponents/sk-table-loader/sk-table-loader.component';
import { finalize } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-spend-intelligence',
  standalone: true,
  imports: [CommonModule, BaseChartDirective, AbbreviatedNumberPipe, MatProgressSpinnerModule, SkTableLoaderComponent, TranslateModule],
  templateUrl: './spend-intelligence.component.html',
  styleUrl: './spend-intelligence.component.scss'
})
export class SpendIntelligenceComponent implements OnInit, OnDestroy {

  /**
   * Active theme palette. Populated synchronously by the palette$ subscription
   * in the constructor (BehaviorSubject emits to new subscribers immediately),
   * so it is defined before any method body or template binding reads it.
   *
   * The `!` assertion is correct because the subscribe call in the constructor
   * runs before any other instance member is accessed externally.
   */
  private p!: ChartPalette;

  private paletteSub?: Subscription;

  totalCategorySpend = 0;
  overallTotalCategorySpend: number | null = null;
  tailSpendTotal = 0;
  overallTailSpendTotal: number | null = null;
  loadingTotalCategorySpend = true;
  loadingTailSpend = true;
  loadingTailDistribution = true;
  // Head of Tail data
  loadingHeadOfTail = true;
  headOfTailTotal = 0;
  headOfTailPercentage = 0;
  headOfTailTopSuppliers: Array<{ name: string; amount: number }> = [];
  // Tail of Tail data
  loadingTailOfTail = true;
  tailOfTailTotal = 0;
  tailOfTailPercentage = 0;
  tailOfTailTopSuppliers: Array<{ name: string; amount: number }> = [];
  // Donut for Head of Tail %. Seeded in `seedDefaultCharts()` so backgroundColor
  // can read from the active theme palette (which isn't available at field-init time).
  headOfTailDonutData!: ChartConfiguration<'doughnut'>['data'];
  headOfTailDonutOptions: ChartConfiguration<'doughnut'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '70%',
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
  };

  headOfTailDonutPlugin = {
    id: 'headOfTailDonutPlugin',
    afterDraw: (chart: any) => {
      if (!chart) return;
      const ctx = chart.ctx;
      const width = chart.width;
      const height = chart.height;
      ctx.save();
      const fontSize = Math.round(Math.min(width, height) / 6);
      ctx.font = `${fontSize}px Arial`;
      ctx.fillStyle = this.p.brandDark;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const text = (this.headOfTailPercentage ? this.headOfTailPercentage.toFixed(2) : '0.00') + '%';
      ctx.fillText(text, width / 2, height / 2);
      ctx.restore();
    }
  };

  // Donut for Tail of Tail %. Seeded in `seedDefaultCharts()`.
  tailOfTailDonutData!: ChartConfiguration<'doughnut'>['data'];
  tailOfTailDonutOptions: ChartConfiguration<'doughnut'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '70%',
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
  };

  tailOfTailDonutPlugin = {
    id: 'tailOfTailDonutPlugin',
    afterDraw: (chart: any) => {
      if (!chart) return;
      const ctx = chart.ctx;
      const width = chart.width;
      const height = chart.height;
      ctx.save();
      const fontSize = Math.round(Math.min(width, height) / 6);
      ctx.font = `${fontSize}px Arial`;
      ctx.fillStyle = this.p.brandDark;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const text = (this.tailOfTailPercentage ? this.tailOfTailPercentage.toFixed(2) : '0.00') + '%';
      ctx.fillText(text, width / 2, height / 2);
      ctx.restore();
    }
  };
  level1CategoryLabel = '';
  level1TotalTransactionAmount = 0;
  loadingLevel1 = true;
  topLevel4: Array<{ X_Level4: string; Y_Total_Transaction_Amount: number; Bubble_Distinct_Parent_Count: number }> = [];
  loadingTopLevel4 = true;
  topLegendItems: Array<{ label: string; color: string }> = [];
  selectedTopLegend: string | null = null;
  // Drill response containers
  abcSummary: any[] = [];
  tailSuppliers: any[] = [];
  yearlyBarData: ChartConfiguration<'bar'>['data'] = { labels: [], datasets: [] };
  loadingYearly = true;
  yearlyBarOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    scales: { x: { grid: { display: false } }, y: { beginAtZero: true } },
    plugins: { legend: { display: false } },
  };
  spendRecommendations: Array<{ category: string; savings_low: number; savings_high: number }> = [];
  loadingRecommendations = true;
  legendItems: Array<{ label: string; color: string }> = [];
  selectedLegend: string | null = null;
  selectedSupplier: string | null = null;
  selectedYear: string | null = null;
  selectedRecommendation: string | null = null;
  selectedDistribution: string | null = null;
  selectedTransactionDonut: string | null = null;
  selectedHeadOfTailSupplier: string | null = null;
  selectedTailOfTailSupplier: string | null = null;
  selectedMonth: string | null = null;
  multiSelectedItems: { type: string; label: string }[] = [];
  monthlyData: any[] = [];

  // Theme-reactive palettes (replace the hardcoded literal arrays that used
  // to live here). Getters resolve to the current ChartPalette so chart drill
  // logic auto-themes — `this.categoryDonutPalette.slice(0, n)` keeps working.
  get categoryDonutPalette(): string[] {
    // 5-slot multi-series palette (donut category slices).
    return this.p.categorical;
  }
  get topCategoriesPalette(): string[] {
    // 5-slot sequential palette (top-N bar chart, dark → light).
    return this.p.sequential;
  }
  get smallTransactionPalette(): string[] {
    // Compact 3-slot palette for the small transaction donut.
    return [this.p.accent, this.p.brandDark, this.p.categorical[2]];
  }

  originalState: any = null;

  constructor(
    private spendIntelligenceService: SpendIntelligenceService,
    private palette: ChartPaletteService,
  ) {
    // Subscribe to the active theme palette. BehaviorSubject emits synchronously
    // on subscribe, so `this.p` is populated before any subsequent code reads it.
    // The same callback re-runs on every theme switch. `recolorActiveCharts`
    // seeds the chart data fields on first call and patches colors on later calls.
    this.paletteSub = this.palette.palette$.subscribe((p) => {
      this.p = p;
      this.recolorActiveCharts();
    });
  }

  ngOnDestroy(): void {
    this.paletteSub?.unsubscribe();
  }

  /**
   * Applies the current theme palette to every chart.
   *
   * Runs on every palette$ emission (initial + theme switches). On the first
   * call the data fields are still `!`-declared, so this method also seeds
   * their structural defaults. On subsequent calls it preserves any labels
   * and data values written by drill API responses, swapping only the color
   * slots. This keeps drill state intact across theme changes.
   */
  private recolorActiveCharts(): void {
    const p = this.p;
    if (!p) return;

    // Head-of-tail donut — 2 slices (positive head segment + dim "rest").
    {
      const prev = this.headOfTailDonutData;
      this.headOfTailDonutData = {
        labels: (prev?.labels as string[] | undefined) ?? ['Head', 'Rest'],
        datasets: [{
          data: (prev?.datasets?.[0]?.data as number[] | undefined) ?? [75, 25],
          hoverOffset: prev?.datasets?.[0]?.hoverOffset ?? 0,
          backgroundColor: [p.head, p.dim],
        }],
      };
    }

    // Tail-of-tail donut — 2 slices (attention tail segment + dim "rest").
    {
      const prev = this.tailOfTailDonutData;
      this.tailOfTailDonutData = {
        labels: (prev?.labels as string[] | undefined) ?? ['Tail', 'Rest'],
        datasets: [{
          data: (prev?.datasets?.[0]?.data as number[] | undefined) ?? [75, 25],
          hoverOffset: prev?.datasets?.[0]?.hoverOffset ?? 0,
          backgroundColor: [p.tail, p.dim],
        }],
      };
    }

    // Category donut — n slices, uses categorical palette.
    if (this.categoryDonutData?.datasets?.[0]) {
      const len = (this.categoryDonutData.labels as string[] | undefined)?.length ?? 0;
      this.categoryDonutData = {
        ...this.categoryDonutData,
        datasets: [{
          ...this.categoryDonutData.datasets[0],
          backgroundColor: this.palette.categorical(len),
        }],
      };
    }

    // Top categories bar — sequential dark→light ramp.
    if (this.topCategoriesBarData?.datasets?.[0]) {
      const len = (this.topCategoriesBarData.labels as string[] | undefined)?.length ?? 0;
      this.topCategoriesBarData = {
        ...this.topCategoriesBarData,
        datasets: [{
          ...this.topCategoriesBarData.datasets[0],
          backgroundColor: this.palette.sequential(len),
        }],
      };
    }

    // Small transaction donut — compact placeholder until drilled data arrives.
    {
      const prev = this.smallTransactionDonutData;
      const labels = (prev?.labels as string[] | undefined) ?? ['Head', 'Tail', 'Other'];
      this.smallTransactionDonutData = {
        labels,
        datasets: [{
          data: (prev?.datasets?.[0]?.data as number[] | undefined) ?? [55, 30, 15],
          hoverOffset: prev?.datasets?.[0]?.hoverOffset ?? 4,
          backgroundColor: this.palette.categorical(labels.length),
        }],
      };
    }

    // Small distribution bar — single accent color, placeholder data until drilled.
    {
      const prev = this.smallDistributionBarData;
      this.smallDistributionBarData = {
        labels: (prev?.labels as string[] | undefined) ?? ['8%', '9%', '8%', '20%', '12%'],
        datasets: [{
          label: 'Share',
          data: (prev?.datasets?.[0]?.data as number[] | undefined) ?? [10, 11, 9, 25, 20],
          backgroundColor: p.accent,
          borderRadius: 6,
          barPercentage: 0.6,
        }],
      };
    }

    // Small distribution bar — options (axis tick colors, grid alpha). Pure
    // theming, no drill state.
    this.smallDistributionBarOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: { display: false },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { display: true, color: p.axisMuted, font: { size: 11 } },
        },
        y: {
          grid: { color: withAlpha(p.axisGrid, 0.6) },
          ticks: {
            display: true,
            color: p.axisMuted,
            font: { size: 11 },
            callback: (value) => `${value}%`,
            stepSize: 5,
          },
          beginAtZero: true,
          suggestedMax: 30,
        },
      },
    };

    // Yearly bar — single brand-dark color (preserve labels/data from drill).
    if (this.yearlyBarData?.datasets?.[0]) {
      this.yearlyBarData = {
        ...this.yearlyBarData,
        datasets: [{
          ...this.yearlyBarData.datasets[0],
          backgroundColor: p.brandDark,
        }],
      };
    }

    // Legend swatches (driven by template — rebuild with current palette indices)
    this.legendItems = this.legendItems.map((item, i) => ({
      ...item,
      color: p.categorical[i % p.categorical.length] ?? p.fallback,
    }));
    this.topLegendItems = this.topLegendItems.map((item, i) => ({
      ...item,
      color: p.sequential[i % p.sequential.length] ?? p.brandDark,
    }));
  }

  ngOnInit() {
    this.loadingTotalCategorySpend = true;
    this.spendIntelligenceService
      .getTotalCategorySpend()
      .pipe(finalize(() => (this.loadingTotalCategorySpend = false)))
      .subscribe(
        (response: any) => {
          if (response && response.data && response.data.length) {
            this.totalCategorySpend = response.data[0].Total_Category_Spend;
            this.overallTotalCategorySpend = Number(response.data[0].Total_Category_Spend) || this.overallTotalCategorySpend;
          }
        },
        (err) => console.error(err)
      );
    this.loadingTailSpend = true;
    this.spendIntelligenceService
      .getTailSpendSuppliers()
      .pipe(finalize(() => (this.loadingTailSpend = false)))
      .subscribe(
        (response: any) => {
          if (response && response.data && response.data.length) {
            this.tailSpendTotal = response.data[0].Tail_Spend_Total;
            this.overallTailSpendTotal = Number(response.data[0].Tail_Spend_Total) || this.overallTailSpendTotal;
          }
        },
        (err) => console.error(err)
      );
    // Tail Spend Distribution by Category -> populate small charts
    this.loadingTailDistribution = true;
    this.spendIntelligenceService
      .getTailSpendDistributionByCategory()
      .pipe(finalize(() => (this.loadingTailDistribution = false)))
      .subscribe(
        (response: any) => {
          if (response && response.data && response.data.length) {
            const data = response.data;
            const labels = data.map((r: any) => r.Level1 || 'Unknown');
            const percentages = data.map((r: any) => Number(r.Tail_Spend_Percentage) || 0);
            const amounts = data.map((r: any) => Number(r.Total_Transaction_Amount) || 0);

            // Update small distribution bar to show tail spend % per category
            this.smallDistributionBarData = {
              labels,
              datasets: [
                {
                  label: 'Tail Spend %',
                  data: percentages,
                  backgroundColor: this.p.accent,
                  borderRadius: 6,
                  barPercentage: 0.6,
                },
              ],
            };

            // Update small donut to show transaction amount share per category
            const colors = this.p.categorical;
            this.smallTransactionDonutData = {
              labels,
              datasets: [
                { data: amounts, backgroundColor: colors.slice(0, labels.length), hoverOffset: 4 },
              ],
            };
          }
        },
        (err) => console.error(err)
      );
    // Head of Tail spend summary + top suppliers
    this.loadingHeadOfTail = true;

    this.spendIntelligenceService
      .getHeadOfTailSpend()
      .pipe(finalize(() => (this.loadingHeadOfTail = false)))
      .subscribe({
        next: (response: any) => {
          
          try {
            const d = response?.data ?? null;

            if (!d) {
              return;
            }

            // Case A: Multiple result sets [rs1, rs2, rs3]
            if (Array.isArray(d) && Array.isArray(d[0])) {
              const rs1: any[] = d[0]; // donut / class split
              const rs2: any[] = d[1]; // B_Class_Total_Spend (single row)
              const rs3: any[] = d[2]; // top 10 suppliers

              // --- Process rs1 (donut/class split)
              if (rs1.length) {
                const labels = rs1.map((r: any) => r.ABC_Class || 'Unknown');
                const values = rs1.map((r: any) => Number(r.Total_Spend || 0));
                const colors = this.p.categorical;

                this.headOfTailDonutData = {
                  labels,
                  datasets: [
                    { data: values, backgroundColor: colors.slice(0, labels.length), hoverOffset: 4 }
                  ]
                };

                // Calculate B class percentage
                const bRow = rs1.find(r => (r.ABC_Class || r.Class || '').toUpperCase() === 'B');
                if (bRow) {
                  const pct = Number(bRow.Percentage ?? 0);
                  if (pct > 0) {
                    this.headOfTailPercentage = pct;
                  } else {
                    const totalSum = rs1.reduce((acc, cur) => acc + Number(cur.Total_Spend || 0), 0);
                    const bSum = Number(bRow.Total_Spend || 0);
                    this.headOfTailPercentage = totalSum ? (bSum * 100) / totalSum : 0;
                  }

                  const headPct = Math.min(Math.max(this.headOfTailPercentage, 0), 100);
                  this.headOfTailDonutData = {
                    labels: ['Head', 'Rest'],
                    datasets: [{ data: [headPct, 100 - headPct], backgroundColor: [this.p.head, this.p.dim], hoverOffset: 0 }]
                  };
                }
              }

              // --- Process rs2 (B class total spend)
              if (rs2.length) {
                const first = rs2[0];
                this.headOfTailTotal = Number(first.B_Class_Total_Spend || 0);
                this.headOfTailPercentage = Number(first.Percentage || this.headOfTailPercentage || 0);
              }

              // --- Process rs3 (top B suppliers)
              if (rs3.length) {
                this.headOfTailTopSuppliers = rs3.map(s => ({
                  name: s.Normalised_Supplier_Name || s.name || 'Unknown',
                  amount: Number(s.Transaction_Amount || s.amount || 0)
                }));
              }

              return;
            }

            // Case B: Flat array of supplier rows
            if (Array.isArray(d) && d[0]?.Normalised_Supplier_Name) {
              this.headOfTailTopSuppliers = d.map(r => ({
                name: r.Normalised_Supplier_Name,
                amount: Number(r.Transaction_Amount) || 0
              }));
              return;
            }

            // Case C: Legacy shape
            if (Array.isArray(response?.suppliers)) {
              this.headOfTailTopSuppliers = response.suppliers.map((s: any) => ({
                name: s.Normalised_Supplier_Name || s.name || 'Unknown',
                amount: Number(s.Transaction_Amount || s.amount || 0)
              }));
            }

          } catch (err) {
          }
        },
        error: err => console.error('Error fetching Head of Tail data', err)
      });
    // Tail of Tail spend summary + top suppliers
    this.loadingTailOfTail = true;

    this.spendIntelligenceService
      .getTailOfTailSpend()
      .pipe(finalize(() => (this.loadingTailOfTail = false)))
      .subscribe({
        next: (response: any) => {
          try {
            const d = response?.data ?? null;

            if (!d) {
              return;
            }

            // Case A: Multiple result sets [rs1, rs2, rs3]
            if (Array.isArray(d) && Array.isArray(d[0])) {
              const rs1: any[] = d[0]; // donut / class split
              const rs2: any[] = d[1]; // C_Class_Total_Spend (single row)
              const rs3: any[] = d[2]; // top C suppliers

              // --- Process rs1 (donut/class split)
              if (rs1.length) {
                const labels = rs1.map((r: any) => r.ABC_Class || 'Unknown');
                const values = rs1.map((r: any) => Number(r.Total_Spend || 0));
                const colors = this.p.categorical;

                this.headOfTailDonutData = {
                  labels,
                  datasets: [
                    { data: values, backgroundColor: colors.slice(0, labels.length), hoverOffset: 4 }
                  ]
                };

                // Calculate B class percentage
                const bRow = rs1.find(r => (r.ABC_Class || r.Class || '').toUpperCase() === 'C');
                if (bRow) {
                  const pct = Number(bRow.Percentage ?? 0);
                  if (pct > 0) {
                    this.tailOfTailPercentage = pct;
                  } else {
                    const totalSum = rs1.reduce((acc, cur) => acc + Number(cur.Total_Spend || 0), 0);
                    const bSum = Number(bRow.Total_Spend || 0);
                    this.tailOfTailPercentage = totalSum ? (bSum * 100) / totalSum : 0;
                  }

                  const headPct = Math.min(Math.max(this.tailOfTailPercentage, 0), 100);
                  this.tailOfTailDonutData = {
                    labels: ['Head', 'Rest'],
                    datasets: [{ data: [headPct, 100 - headPct], backgroundColor: [this.p.head, this.p.dim], hoverOffset: 0 }]
                  };
                }
              }

              // --- Process rs2 (C class total spend)
              if (rs2.length) {
                this.tailOfTailTotal = Number(rs2[0].C_Class_Total_Spend) || 0;
              }

              // --- Process rs3 (top C suppliers)
              if (rs3.length) {
                this.tailOfTailTopSuppliers = rs3.map((r: any) => ({
                  name: r.Normalised_Supplier_Name || 'Unknown',
                  amount: Number(r.Transaction_Amount) || 0
                }));
              }

              return;
            }

            // Case B: Flat array of supplier rows
            if (Array.isArray(d) && d[0]?.Normalised_Supplier_Name) {
              this.tailOfTailTopSuppliers = d.map(r => ({
                name: r.Normalised_Supplier_Name,
                amount: Number(r.Transaction_Amount) || 0
              }));
              return;
            }

            // Case C: Legacy shape
            if (Array.isArray(response?.suppliers)) {
              this.tailOfTailTopSuppliers = response.suppliers.map((s: any) => ({
                name: s.Normalised_Supplier_Name || s.name || 'Unknown',
                amount: Number(s.Transaction_Amount || s.amount || 0)
              }));
            }

          } catch (err) {
          }
        },
        error: err => console.error('Error fetching Tail of Tail data', err)
      });
    this.loadingLevel1 = true;
    this.spendIntelligenceService
      .getSpendByLevel1CategoryBreakdown()
      .pipe(finalize(() => (this.loadingLevel1 = false)))
      .subscribe(
        (response: any) => {
          if (response && response.data && response.data.length) {
            const data = response.data;
            const first = data[0];
            this.level1CategoryLabel = first.Level1 || '';
            this.level1TotalTransactionAmount = first.Total_Transaction_Amount || 0;

            // Populate category donut chart from Level1 breakdown
            const labels = data.map((r: any) => r.Level1 || 'Unknown');
            const values = data.map((r: any) => r.Total_Transaction_Amount || 0);
            // Reuse the same color palette (will repeat if more than 5)
            const backgroundColor = this.p.categorical;
            this.categoryDonutData = {
              labels,
              datasets: [{ data: values, backgroundColor: backgroundColor.slice(0, labels.length), hoverOffset: 6 }],
            };
            // update interactive legend items
            this.legendItems = labels.map((lbl: string, i: number) => ({ label: lbl, color: (backgroundColor.slice(0, labels.length) as string[])[i] || this.p.fallback }));
          }
        },
        (err) => console.error(err)
      );
    this.loadingTopLevel4 = true;
    this.spendIntelligenceService
      .getTop5Level4SpendCategory()
      .pipe(finalize(() => (this.loadingTopLevel4 = false)))
      .subscribe(
        (response: any) => {
          if (response && response.data) {
            this.topLevel4 = response.data;

            // Build Top 5 bar chart data
            const labels = this.topLevel4.map((r) => r.X_Level4 || 'Unknown');
            const values = this.topLevel4.map((r) => r.Y_Total_Transaction_Amount || 0);
            // colors for bars
            const colors = this.p.sequential;
            this.topCategoriesBarData = { labels, datasets: [{ label: 'Spend', data: values, backgroundColor: colors.slice(0, labels.length) }] };

            // build legend for top categories
            this.topLegendItems = labels.map((lbl: string, i: number) => ({ label: lbl, color: colors[i] || this.p.brandDark }));
          }
        },
        (err) => console.error(err)
      );
    this.loadingYearly = true;
    this.spendIntelligenceService
      .getYearlySpendCategory()
      .pipe(finalize(() => (this.loadingYearly = false)))
      .subscribe(
        (response: any) => {
          if (response && response.data) {
            const labels = response.data.map((r: any) => String(r.Year));
            const values = response.data.map((r: any) => r.Total_Spend || 0);
            this.yearlyBarData = { labels, datasets: [{ label: 'Total Spend', data: values, backgroundColor: this.p.brandDark }] };
          }
        },
        (err) => console.error(err)
      );
    this.loadingRecommendations = true;
    this.spendIntelligenceService
      .getSpendRecommendationsCategory()
      .pipe(finalize(() => (this.loadingRecommendations = false)))
      .subscribe(
        (response: any) => {
          if (response && response.data) {
            this.spendRecommendations = response.data;
          }
        },
        (err) => console.error(err)
      );
  }

  backupInitialState() {
  this.originalState = structuredClone({
    // KPIs
    totalCategorySpend: this.totalCategorySpend,
    tailSpendTotal: this.tailSpendTotal,
    overallTotalCategorySpend: this.overallTotalCategorySpend,
    overallTailSpendTotal: this.overallTailSpendTotal,

    // Charts
    categoryDonutData: this.categoryDonutData,
    topCategoriesBarData: this.topCategoriesBarData,
    smallTransactionDonutData: this.smallTransactionDonutData,
    smallDistributionBarData: this.smallDistributionBarData,
    yearlyBarData: this.yearlyBarData,

    // 9 result sets mapping
    abcSummary: this.abcSummary,
    tailSuppliers: this.tailSuppliers,
    spendRecommendations: this.spendRecommendations,

    headOfTailTotal: this.headOfTailTotal,
    headOfTailPercentage: this.headOfTailPercentage,
    headOfTailTopSuppliers: this.headOfTailTopSuppliers,
    headOfTailDonutData: this.headOfTailDonutData,

    tailOfTailTotal: this.tailOfTailTotal,
    tailOfTailPercentage: this.tailOfTailPercentage,
    tailOfTailTopSuppliers: this.tailOfTailTopSuppliers,
    tailOfTailDonutData: this.tailOfTailDonutData,

    // Level1
    level1CategoryLabel: this.level1CategoryLabel,
    level1TotalTransactionAmount: this.level1TotalTransactionAmount,

    selectedYear: this.selectedYear,
    selectedMonth: this.selectedMonth,
    selectedRecommendation: this.selectedRecommendation,
    selectedDistribution: this.selectedDistribution,
    selectedTransactionDonut: this.selectedTransactionDonut,
    selectedHeadOfTailSupplier: this.selectedHeadOfTailSupplier,
    selectedTailOfTailSupplier: this.selectedTailOfTailSupplier,

    // legends
    legendItems: this.legendItems,
    topLegendItems: this.topLegendItems,

    // Top 5 Categories bar bubbles
    topLevel4: this.topLevel4,
  });
}

  selectLegend(index: number) {
    const item = this.legendItems[index];
    if (!item) return;
    this.drillByLevel1(item.label);
  }

  // Chart click handlers and filter logic
  onCategoryDonutClick(event: any) {
    const active = event?.active;
    if (!active || !active.length) {
      this.clearFilter();
      return;
    }
    const idx = active[0].index;
    const label = (this.categoryDonutData.labels as string[])[idx];
    const isCtrl = !!(event?.event?.native?.ctrlKey || event?.event?.native?.metaKey);
    if (this.handleMultiSelectToggle('Level1', label, isCtrl)) return;
    this.drillByLevel1(label);
  }

  drillByLevel1(label: string) {
    // Toggle: clicking same slice again resets to original state
    if (this.selectedLegend === label) {
      this.clearFilter();
      return;
    }

    // Backup initial state only once (before the very first drill)
    if (!this.originalState) {
      this.backupInitialState();
    }

    this.selectedLegend = label;
    this.selectedTopLegend = null; // clear any bar selection

    // Highlight selected slice, dim others immediately
    try {
      const labels = (this.categoryDonutData.labels as string[]) || [];
      const bg = labels.map((l, i) => (l === label ? this.categoryDonutPalette[i % this.categoryDonutPalette.length] : this.p.dim));
      this.categoryDonutData = { ...this.categoryDonutData, datasets: [{ ...this.categoryDonutData.datasets[0], backgroundColor: bg }] };
    } catch (e) { }

    // Drill API: fetch all visuals for the selected Level1 category
    this.spendIntelligenceService
      .getSpendIntelligenceDrillByLevel1Category({ Level1Name: label })
      .subscribe({
        next: (response: any) => {
          
          try {
            const d = response?.data || [];

            // d[0]: Tail Spend Total
            const tailSpendVal = d[0]?.[0]?.Tail_Spend_Total ?? null;
            if (tailSpendVal !== null) this.tailSpendTotal = Number(tailSpendVal);

            // d[1]: ABC Summary → always update both head/tail donuts
            const abcArr = d[1] || [];
            this.abcSummary = abcArr;
            {
              const bRow = abcArr.find((r: any) => (r.ABC_Class || '').toUpperCase() === 'B');
              const cRow = abcArr.find((r: any) => (r.ABC_Class || '').toUpperCase() === 'C');
              const bPct = bRow ? Number(bRow.Percentage ?? 0) : 0;
              this.headOfTailPercentage = bPct;
              const headPct = Math.min(Math.max(bPct, 0), 100);
              this.headOfTailDonutData = { labels: ['Head', 'Rest'], datasets: [{ data: [headPct, 100 - headPct], backgroundColor: [this.p.head, this.p.dim], hoverOffset: 0 }] };
              const cPct = cRow ? Number(cRow.Percentage ?? 0) : 0;
              this.tailOfTailPercentage = cPct;
              const tailPct = Math.min(Math.max(cPct, 0), 100);
              this.tailOfTailDonutData = { labels: ['Tail', 'Rest'], datasets: [{ data: [tailPct, 100 - tailPct], backgroundColor: [this.p.tail, this.p.dim], hoverOffset: 0 }] };
            }

            // d[2]: Suppliers by ABC_Class B/C
            const suppArr = d[2] || [];
            this.tailSuppliers = suppArr;
            this.headOfTailTopSuppliers = suppArr
              .filter((s: any) => s?.ABC_Class === 'B')
              .map((s: any) => ({ name: s.Normalised_Supplier_Name, amount: Number(s.Supplier_Total_Spend || s.Transaction_Amount) || 0 }));
            this.tailOfTailTopSuppliers = suppArr
              .filter((s: any) => s?.ABC_Class === 'C')
              .map((s: any) => ({ name: s.Normalised_Supplier_Name, amount: Number(s.Supplier_Total_Spend || s.Transaction_Amount) || 0 }));

            // d[3]: Level2 breakdown → redraw category donut
            const level2Arr = d[3] || [];
            if (level2Arr.length) {
              const lvl2Labels = level2Arr.map((r: any) => r.Level2 || 'Unknown');
              const lvl2Values = level2Arr.map((r: any) => Number(r.Total_Transaction_Amount || 0));
              const lvl2Colors = this.categoryDonutPalette.slice(0, lvl2Labels.length);
              this.categoryDonutData = {
                labels: lvl2Labels,
                datasets: [{ data: lvl2Values, backgroundColor: lvl2Colors, hoverOffset: 4 }]
              };
              this.legendItems = lvl2Labels.map((lbl: string, i: number) => ({ label: lbl, color: lvl2Colors[i] || this.p.brandDark }));
              const tColors1 = this.p.categorical;
              this.smallTransactionDonutData = { labels: lvl2Labels, datasets: [{ data: lvl2Values, backgroundColor: tColors1.slice(0, lvl2Labels.length), hoverOffset: 4 }] };
            }

            // d[4]: Top 5 Level4 categories
            const top5Arr = d[4] || [];
            {
              const topLabels = top5Arr.map((r: any) => r.X_Level4 || 'Unknown');
              const topValues = top5Arr.map((r: any) => r.Y_Total_Transaction_Amount || 0);
              this.topLevel4 = top5Arr;
              this.topCategoriesBarData = {
                labels: topLabels,
                datasets: [{ label: 'Spend', data: topValues, backgroundColor: this.topCategoriesPalette.slice(0, topLabels.length) }],
              };
              this.topLegendItems = topLabels.map((lbl: string, i: number) => ({ label: lbl, color: this.topCategoriesPalette[i] || this.p.brandDark }));
            }

            // d[5]: Yearly Spend
            const yearlyArr = d[5] || [];
            if (yearlyArr.length) {
              this.yearlyBarData = {
                labels: yearlyArr.map((r: any) => String(r.Year)),
                datasets: [{ label: 'Total Spend', data: yearlyArr.map((r: any) => r.Total_Spend || 0), backgroundColor: this.p.brandDark }],
              };
            }

            // d[6]: Spend Recommendations
            this.spendRecommendations = d[6] || [];

            // d[7]: Level2 tail distribution
            const distArr = d[7] || [];
            if (distArr.length) {
              this.smallDistributionBarData = {
                labels: distArr.map((r: any) => r.Level2 || 'Unknown'),
                datasets: [{ label: 'Share', data: distArr.map((r: any) => r.Tail_Spend_Percentage ?? 0), backgroundColor: this.p.accent, borderRadius: 6, barPercentage: 0.6 }],
              };
            }

            // d[8]: Head of Tail total
            if (d[8]?.length) this.headOfTailTotal = Number(d[8][0]?.HeadOfTailSpend) || 0;

            // d[9]: Tail of Tail total
            if (d[9]?.length) this.tailOfTailTotal = Number(d[9][0]?.TailOfTailSpend) || 0;

          } catch (err) {
          }
        },
        error: err => console.error('Level1 Drill API error', err)
      });
  }

  onTopCategoriesClick(event: any) {
    const active = event?.active;
    if (!active || !active.length) {
      this.clearFilter();
      return;
    }

    const idx = active[0].index;
    const label = (this.topCategoriesBarData.labels as string[])[idx];
    const isCtrl = !!(event?.event?.native?.ctrlKey || event?.event?.native?.metaKey);
    if (this.handleMultiSelectToggle('Level4', label, isCtrl)) return;

    // Toggle: clicking the same bar again resets to original state
    if (this.selectedTopLegend === label) {
      this.clearFilter();
      return;
    }

    // Backup initial state only once (before the very first drill)
    if (!this.originalState) {
      this.backupInitialState();
    }

    this.selectedTopLegend = label;
    this.selectedLegend = label;

    // Highlight selected bar, dim others immediately
    try {
      const labels = (this.topCategoriesBarData.labels as string[]) || [];
      const bg = labels.map((l, i) => (l === label ? this.topCategoriesPalette[i % this.topCategoriesPalette.length] : this.p.dim));
      this.topCategoriesBarData = { ...this.topCategoriesBarData, datasets: [{ ...this.topCategoriesBarData.datasets[0], backgroundColor: bg }] };
    } catch (e) { }

    // Drill API: fetch all visuals for the selected Level4 category
    this.spendIntelligenceService
      .getSpendIntelligenceDrillByLevel4Taxonomy({ Level4Name: label })
      .subscribe({
        next: (response: any) => {
          try {
            const d = response?.data || [];

            // d[0]: Total Category Spend + Tail Spend Total
            const totalCatVal = d[0]?.[0]?.Total_Category_Spend ?? null;
            if (totalCatVal !== null) this.totalCategorySpend = Number(totalCatVal);
            const tailSpendVal = d[0]?.[0]?.Tail_Spend_Total ?? null;
            if (tailSpendVal !== null) this.tailSpendTotal = Number(tailSpendVal);

            // d[1]: ABC Summary -> always update both head/tail donuts
            const abcArr = d[1] || [];
            this.abcSummary = abcArr;
            {
              const bRow = abcArr.find((r: any) => (r.ABC_Class || '').toUpperCase() === 'B');
              const cRow = abcArr.find((r: any) => (r.ABC_Class || '').toUpperCase() === 'C');
              const bPct = bRow ? Number(bRow.Percentage ?? 0) : 0;
              this.headOfTailPercentage = bPct;
              const headPct = Math.min(Math.max(bPct, 0), 100);
              this.headOfTailDonutData = { labels: ['Head', 'Rest'], datasets: [{ data: [headPct, 100 - headPct], backgroundColor: [this.p.head, this.p.dim], hoverOffset: 0 }] };
              const cPct = cRow ? Number(cRow.Percentage ?? 0) : 0;
              this.tailOfTailPercentage = cPct;
              const tailPct = Math.min(Math.max(cPct, 0), 100);
              this.tailOfTailDonutData = { labels: ['Tail', 'Rest'], datasets: [{ data: [tailPct, 100 - tailPct], backgroundColor: [this.p.tail, this.p.dim], hoverOffset: 0 }] };
            }

            // d[2]: Suppliers by ABC_Class B/C
            const suppArr = d[2] || [];
            this.tailSuppliers = suppArr;
            this.headOfTailTopSuppliers = suppArr
              .filter((s: any) => s?.ABC_Class === 'B')
              .map((s: any) => ({ name: s.Normalised_Supplier_Name, amount: Number(s.Supplier_Total_Spend || s.Transaction_Amount) || 0 }));
            this.tailOfTailTopSuppliers = suppArr
              .filter((s: any) => s?.ABC_Class === 'C')
              .map((s: any) => ({ name: s.Normalised_Supplier_Name, amount: Number(s.Supplier_Total_Spend || s.Transaction_Amount) || 0 }));

            // d[3]: Level1 Category Breakdown -> redraw category donut
            const level1Arr = d[3] || [];
            if (level1Arr.length) {
              const labels = level1Arr.map((r: any) => r.Level1 || 'Unknown');
              const values = level1Arr.map((r: any) => r.Total_Transaction_Amount || 0);
              this.categoryDonutData = {
                labels,
                datasets: [{ data: values, backgroundColor: this.categoryDonutPalette.slice(0, labels.length), hoverOffset: 6 }],
              };
              this.legendItems = labels.map((lbl: string, i: number) => ({ label: lbl, color: this.categoryDonutPalette[i] || this.p.fallback }));
              this.level1CategoryLabel = level1Arr[0].Level1 || '';
              this.level1TotalTransactionAmount = level1Arr[0].Total_Transaction_Amount || 0;
              const tColors4 = this.p.categorical;
              this.smallTransactionDonutData = { labels, datasets: [{ data: values, backgroundColor: tColors4.slice(0, labels.length), hoverOffset: 4 }] };
            }

            // d[4]: Yearly Spend
            const yearlyArr = d[4] || [];
            if (yearlyArr.length) {
              this.yearlyBarData = {
                labels: yearlyArr.map((r: any) => String(r.Year)),
                datasets: [{ label: 'Total Spend', data: yearlyArr.map((r: any) => r.Total_Spend || 0), backgroundColor: this.p.brandDark }],
              };
            }

            // d[5]: Spend Recommendations
            this.spendRecommendations = d[5] || [];

            // d[6]: Level1 tail distribution
            const distArr = d[6] || [];
            if (distArr.length) {
              this.smallDistributionBarData = {
                labels: distArr.map((r: any) => r.Level1 || 'Unknown'),
                datasets: [{ label: 'Share', data: distArr.map((r: any) => r.Tail_Spend_Percentage ?? 0), backgroundColor: this.p.accent, borderRadius: 6, barPercentage: 0.6 }],
              };
            }

            // d[7]: Head of Tail total (may be empty string)
            if (d[7]?.length) { const hVal = d[7][0]?.HeadOfTailSpend; this.headOfTailTotal = (hVal !== '' && hVal != null) ? Number(hVal) || 0 : 0; }

            // d[8]: Tail of Tail total
            if (d[8]?.length) this.tailOfTailTotal = Number(d[8][0]?.TailOfTailSpend) || 0;

          } catch (err) {
          }
        },
        error: err => console.error('Drill API error', err)
      });
  }

  onTopLegendClick(label: string) {
    if (this.selectedTopLegend === label) {
      this.clearFilter();
    } else {
      // re-use the bar click logic by simulating a selection on the matching bar index
      const labels = (this.topCategoriesBarData.labels as string[]) || [];
      const fakeIdx = labels.indexOf(label);
      if (fakeIdx >= 0) {
        this.onTopCategoriesClick({ active: [{ index: fakeIdx }] });
      }
    }
  }

  onRecommendationClick(row: any, event?: MouseEvent) {
    const isCtrl = !!(event?.ctrlKey || event?.metaKey);
    if (this.handleMultiSelectToggle('Supplier', row.category, isCtrl)) return;
    if (!row) return;
    const label = row.category || null;
    if (!label) return;

    // Toggle: clicking the same recommendation again resets
    if (this.selectedRecommendation === label) {
      this.clearFilter();
      return;
    }

    // Backup initial state only once
    if (!this.originalState) {
      this.backupInitialState();
    }

    this.selectedRecommendation = label;
    this.selectedLegend = null;
    this.selectedTopLegend = null;
    this.selectedYear = null;
    this.selectedMonth = null;

    this.spendIntelligenceService
      .getSpendIntelligenceDrillByRecommendationCategory({ Category: label })
      .subscribe({
        next: (response: any) => {
          try {
            const d = response?.data || [];

            // d[0]: Metadata (Selected_Category, Drill_Level) – skip

            // d[1]: KPIs
            const totalCatVal = d[1]?.[0]?.Total_Category_Spend ?? null;
            if (totalCatVal !== null) this.totalCategorySpend = Number(totalCatVal);
            const tailSpendVal = d[1]?.[0]?.Tail_Spend_Total ?? null;
            if (tailSpendVal !== null) this.tailSpendTotal = Number(tailSpendVal);

            // d[2]: Detailed recommendation row – skip (display-only, not charted)

            // d[3]: ABC Summary -> head/tail donuts
            const abcArr = d[3] || [];
            this.abcSummary = abcArr;
            {
              const bRow = abcArr.find((r: any) => (r.ABC_Class || '').toUpperCase() === 'B');
              const cRow = abcArr.find((r: any) => (r.ABC_Class || '').toUpperCase() === 'C');
              const bPct = bRow ? Number(bRow.Percentage ?? 0) : 0;
              this.headOfTailPercentage = bPct;
              const headPct = Math.min(Math.max(bPct, 0), 100);
              this.headOfTailDonutData = { labels: ['Head', 'Rest'], datasets: [{ data: [headPct, 100 - headPct], backgroundColor: [this.p.head, this.p.dim], hoverOffset: 0 }] };
              const cPct = cRow ? Number(cRow.Percentage ?? 0) : 0;
              this.tailOfTailPercentage = cPct;
              const tailPct = Math.min(Math.max(cPct, 0), 100);
              this.tailOfTailDonutData = { labels: ['Tail', 'Rest'], datasets: [{ data: [tailPct, 100 - tailPct], backgroundColor: [this.p.tail, this.p.dim], hoverOffset: 0 }] };
            }

            // d[4]: Suppliers by ABC_Class B/C
            const suppArr = d[4] || [];
            this.tailSuppliers = suppArr;
            this.headOfTailTopSuppliers = suppArr
              .filter((s: any) => s?.ABC_Class === 'B')
              .map((s: any) => ({ name: s.Normalised_Supplier_Name, amount: Number(s.Supplier_Total_Spend || 0) }));
            this.tailOfTailTopSuppliers = suppArr
              .filter((s: any) => s?.ABC_Class === 'C')
              .map((s: any) => ({ name: s.Normalised_Supplier_Name, amount: Number(s.Supplier_Total_Spend || 0) }));

            // d[5]: Level1/Level2 breakdown -> category donut (use Level2 as label)
            const catArr = d[5] || [];
            if (catArr.length) {
              const labels = catArr.map((r: any) => r.Level2 || r.Level1 || 'Unknown');
              const values = catArr.map((r: any) => Number(r.Total_Transaction_Amount || 0));
              this.categoryDonutData = {
                labels,
                datasets: [{ data: values, backgroundColor: this.categoryDonutPalette.slice(0, labels.length), hoverOffset: 6 }],
              };
              this.legendItems = labels.map((lbl: string, i: number) => ({ label: lbl, color: this.categoryDonutPalette[i] || this.p.fallback }));
              const tColors = this.p.categorical;
              this.smallTransactionDonutData = { labels, datasets: [{ data: values, backgroundColor: tColors.slice(0, labels.length), hoverOffset: 4 }] };
            }

            // d[6]: Yearly Spend
            const yearlyArr = d[6] || [];
            if (yearlyArr.length) {
              this.yearlyBarData = {
                labels: yearlyArr.map((r: any) => String(r.Year)),
                datasets: [{ label: 'Total Spend', data: yearlyArr.map((r: any) => r.Total_Spend || 0), backgroundColor: this.p.brandDark }],
              };
            }

            // d[7]: Top 5 Level4 categories
            const top5Arr = d[7] || [];
            {
              const topLabels = top5Arr.map((r: any) => r.X_Level4 || 'Unknown');
              const topValues = top5Arr.map((r: any) => r.Y_Total_Transaction_Amount || 0);
              this.topLevel4 = top5Arr;
              this.topCategoriesBarData = {
                labels: topLabels,
                datasets: [{ label: 'Spend', data: topValues, backgroundColor: this.topCategoriesPalette.slice(0, topLabels.length) }],
              };
              this.topLegendItems = topLabels.map((lbl: string, i: number) => ({ label: lbl, color: this.topCategoriesPalette[i] || this.p.brandDark }));
            }

            // d[8]: Level1 tail distribution
            const distArr = d[8] || [];
            if (distArr.length) {
              this.smallDistributionBarData = {
                labels: distArr.map((r: any) => r.Level1 || 'Unknown'),
                datasets: [{ label: 'Share', data: distArr.map((r: any) => r.Tail_Spend_Percentage ?? 0), backgroundColor: this.p.accent, borderRadius: 6, barPercentage: 0.6 }],
              };
            }

            // d[9]: Head of Tail total
            if (d[9]?.length) { const hVal = d[9][0]?.HeadOfTailSpend; this.headOfTailTotal = (hVal !== '' && hVal != null) ? Number(hVal) || 0 : 0; }

            // d[10]: Tail of Tail total
            if (d[10]?.length) this.tailOfTailTotal = Number(d[10][0]?.TailOfTailSpend) || 0;

          } catch (err) {
          }
        },
        error: err => console.error('Recommendation Drill API error', err)
      });
  }

  onDistributionBarClick(event: any) {
    const active = event?.active;
    if (!active || !active.length) {
      this.clearFilter();
      return;
    }
    const idx = active[0].index;
    const label = (this.smallDistributionBarData.labels as string[])[idx];
    const isCtrlD = !!(event?.event?.native?.ctrlKey || event?.event?.native?.metaKey);
    if (this.handleMultiSelectToggle('TailSpendLevel1', label, isCtrlD)) return;

    // Toggle: clicking the same bar again resets
    if (this.selectedDistribution === label) {
      this.clearFilter();
      return;
    }

    // Backup initial state only once
    if (!this.originalState) {
      this.backupInitialState();
    }

    this.selectedDistribution = label;
    this.selectedLegend = null;
    this.selectedTopLegend = null;
    this.selectedYear = null;
    this.selectedMonth = null;
    this.selectedRecommendation = null;

    // Highlight selected bar, dim others
    try {
      const labels = (this.smallDistributionBarData.labels as string[]) || [];
      const bg = labels.map(l => l === label ? this.p.accent : this.p.dim);
      this.smallDistributionBarData = { ...this.smallDistributionBarData, datasets: [{ ...this.smallDistributionBarData.datasets[0], backgroundColor: bg }] };
    } catch (e) { }

    this.spendIntelligenceService
      .getSpendIntelligenceDrillByTailSpendLevel1({ Level1Name: label })
      .subscribe({
        next: (response: any) => {
          try {
            const d = response?.data || [];

            // d[0]: Metadata – skip

            // d[1]: KPIs
            const totalCatVal = d[1]?.[0]?.Total_Category_Spend ?? null;
            if (totalCatVal !== null) this.totalCategorySpend = Number(totalCatVal);
            const tailSpendVal = d[1]?.[0]?.Tail_Spend_Total ?? null;
            if (tailSpendVal !== null) this.tailSpendTotal = Number(tailSpendVal);

            // d[2]: ABC Summary -> head/tail donuts
            const abcArr = d[2] || [];
            this.abcSummary = abcArr;
            {
              const bRow = abcArr.find((r: any) => (r.ABC_Class || '').toUpperCase() === 'B');
              const cRow = abcArr.find((r: any) => (r.ABC_Class || '').toUpperCase() === 'C');
              const bPct = bRow ? Number(bRow.Percentage ?? 0) : 0;
              this.headOfTailPercentage = bPct;
              const headPct = Math.min(Math.max(bPct, 0), 100);
              this.headOfTailDonutData = { labels: ['Head', 'Rest'], datasets: [{ data: [headPct, 100 - headPct], backgroundColor: [this.p.head, this.p.dim], hoverOffset: 0 }] };
              const cPct = cRow ? Number(cRow.Percentage ?? 0) : 0;
              this.tailOfTailPercentage = cPct;
              const tailPct = Math.min(Math.max(cPct, 0), 100);
              this.tailOfTailDonutData = { labels: ['Tail', 'Rest'], datasets: [{ data: [tailPct, 100 - tailPct], backgroundColor: [this.p.tail, this.p.dim], hoverOffset: 0 }] };
            }

            // d[3]: Suppliers B/C
            const suppArr = d[3] || [];
            this.tailSuppliers = suppArr;
            this.headOfTailTopSuppliers = suppArr
              .filter((s: any) => s?.ABC_Class === 'B')
              .map((s: any) => ({ name: s.Normalised_Supplier_Name, amount: Number(s.Supplier_Total_Spend || 0) }));
            this.tailOfTailTopSuppliers = suppArr
              .filter((s: any) => s?.ABC_Class === 'C')
              .map((s: any) => ({ name: s.Normalised_Supplier_Name, amount: Number(s.Supplier_Total_Spend || 0) }));

            // d[4]: Level2 tail distribution (Tail_Spend_Percentage) -> smallDistributionBarData
            const distArr = d[4] || [];
            if (distArr.length) {
              const distLabels = distArr.map((r: any) => r.Level2 || 'Unknown');
              const distColors = distLabels.map((l: string) => l === label ? this.p.accent : this.p.dim);
              this.smallDistributionBarData = {
                labels: distLabels,
                datasets: [{ label: 'Share', data: distArr.map((r: any) => r.Tail_Spend_Percentage ?? 0), backgroundColor: distColors, borderRadius: 6, barPercentage: 0.6 }],
              };
            }

            // d[5]: Level2 full breakdown -> category donut
            const level2Arr = d[5] || [];
            if (level2Arr.length) {
              const lvlLabels = level2Arr.map((r: any) => r.Level2 || 'Unknown');
              const lvlValues = level2Arr.map((r: any) => Number(r.Total_Transaction_Amount || 0));
              this.categoryDonutData = {
                labels: lvlLabels,
                datasets: [{ data: lvlValues, backgroundColor: this.categoryDonutPalette.slice(0, lvlLabels.length), hoverOffset: 6 }],
              };
              this.legendItems = lvlLabels.map((lbl: string, i: number) => ({ label: lbl, color: this.categoryDonutPalette[i] || this.p.fallback }));
              const tColors = this.p.categorical;
              this.smallTransactionDonutData = { labels: lvlLabels, datasets: [{ data: lvlValues, backgroundColor: tColors.slice(0, lvlLabels.length), hoverOffset: 4 }] };
            }

            // d[6]: Top 5 Level4 categories
            const top5Arr = d[6] || [];
            {
              const topLabels = top5Arr.map((r: any) => r.X_Level4 || 'Unknown');
              const topValues = top5Arr.map((r: any) => r.Y_Total_Transaction_Amount || 0);
              this.topLevel4 = top5Arr;
              this.topCategoriesBarData = {
                labels: topLabels,
                datasets: [{ label: 'Spend', data: topValues, backgroundColor: this.topCategoriesPalette.slice(0, topLabels.length) }],
              };
              this.topLegendItems = topLabels.map((lbl: string, i: number) => ({ label: lbl, color: this.topCategoriesPalette[i] || this.p.brandDark }));
            }

            // d[7]: Yearly Spend
            const yearlyArr = d[7] || [];
            if (yearlyArr.length) {
              this.yearlyBarData = {
                labels: yearlyArr.map((r: any) => String(r.Year)),
                datasets: [{ label: 'Total Spend', data: yearlyArr.map((r: any) => r.Total_Spend || 0), backgroundColor: this.p.brandDark }],
              };
            }

            // d[8]: Spend Recommendations
            this.spendRecommendations = d[8] || [];

            // d[9]: Head of Tail total
            if (d[9]?.length) { const hVal = d[9][0]?.HeadOfTailSpend; this.headOfTailTotal = (hVal !== '' && hVal != null) ? Number(hVal) || 0 : 0; }

            // d[10]: Tail of Tail total
            if (d[10]?.length) this.tailOfTailTotal = Number(d[10][0]?.TailOfTailSpend) || 0;
          } catch (err) {
          }
        },
        error: err => console.error('TailSpendLevel1 Drill API error', err)
      });
  }

  onSmallTransactionDonutClick(event: any) {
    const active = event?.active;
    if (!active || !active.length) { this.clearFilter(); return; }
    const idx = active[0].index;
    const label = (this.smallTransactionDonutData.labels as string[])[idx];

    if (this.selectedTransactionDonut === label) { this.clearFilter(); return; }

    if (!this.originalState) { this.backupInitialState(); }

    this.selectedTransactionDonut = label;
    this.selectedLegend = null;
    this.selectedTopLegend = null;
    this.selectedYear = null;
    this.selectedMonth = null;
    this.selectedRecommendation = null;
    this.selectedDistribution = null;

    // Highlight selected slice, dim others
    try {
      const tColors = this.p.categorical;
      const lbls = (this.smallTransactionDonutData.labels as string[]) || [];
      const bg = lbls.map((l, i) => l === label ? tColors[i % tColors.length] : this.p.dim);
      this.smallTransactionDonutData = { ...this.smallTransactionDonutData, datasets: [{ ...this.smallTransactionDonutData.datasets[0], backgroundColor: bg }] };
    } catch (e) { }

    this.spendIntelligenceService
      .getSpendIntelligenceDrillByTailSpendLevel1({ Level1Name: label })
      .subscribe({
        next: (response: any) => {
          try {
            const d = response?.data || [];

            // d[1]: KPIs
            const tv = d[1]?.[0]?.Total_Category_Spend ?? null;
            if (tv !== null) this.totalCategorySpend = Number(tv);
            const tsv = d[1]?.[0]?.Tail_Spend_Total ?? null;
            if (tsv !== null) this.tailSpendTotal = Number(tsv);

            // d[2]: ABC -> donuts
            const abcArr = d[2] || [];
            this.abcSummary = abcArr;
            { const bRow = abcArr.find((r: any) => (r.ABC_Class||'').toUpperCase()==='B');
              const cRow = abcArr.find((r: any) => (r.ABC_Class||'').toUpperCase()==='C');
              const bPct = bRow ? Number(bRow.Percentage ?? 0) : 0;
              this.headOfTailPercentage = bPct;
              this.headOfTailDonutData = { labels: ['Head','Rest'], datasets: [{ data: [Math.min(Math.max(bPct,0),100), 100-Math.min(Math.max(bPct,0),100)], backgroundColor: [this.p.head,this.p.dim], hoverOffset: 0 }] };
              const cPct = cRow ? Number(cRow.Percentage ?? 0) : 0;
              this.tailOfTailPercentage = cPct;
              this.tailOfTailDonutData = { labels: ['Tail','Rest'], datasets: [{ data: [Math.min(Math.max(cPct,0),100), 100-Math.min(Math.max(cPct,0),100)], backgroundColor: [this.p.tail,this.p.dim], hoverOffset: 0 }] };
            }

            // d[3]: Suppliers
            const suppArr = d[3] || [];
            this.tailSuppliers = suppArr;
            this.headOfTailTopSuppliers = suppArr.filter((s:any) => s?.ABC_Class==='B').map((s:any) => ({ name: s.Normalised_Supplier_Name, amount: Number(s.Supplier_Total_Spend||0) }));
            this.tailOfTailTopSuppliers = suppArr.filter((s:any) => s?.ABC_Class==='C').map((s:any) => ({ name: s.Normalised_Supplier_Name, amount: Number(s.Supplier_Total_Spend||0) }));

            // d[4]: Level2 tail distribution
            const distArr = d[4] || [];
            if (distArr.length) {
              this.smallDistributionBarData = {
                labels: distArr.map((r:any) => r.Level2||'Unknown'),
                datasets: [{ label: 'Share', data: distArr.map((r:any) => r.Tail_Spend_Percentage ?? 0), backgroundColor: this.p.accent, borderRadius: 6, barPercentage: 0.6 }]
              };
            }

            // d[5]: Level2 full breakdown -> category donut
            const level2Arr = d[5] || [];
            if (level2Arr.length) {
              const ll = level2Arr.map((r:any) => r.Level2||'Unknown');
              const lv = level2Arr.map((r:any) => Number(r.Total_Transaction_Amount||0));
              this.categoryDonutData = { labels: ll, datasets: [{ data: lv, backgroundColor: this.categoryDonutPalette.slice(0, ll.length), hoverOffset: 6 }] };
              this.legendItems = ll.map((lbl:string, i:number) => ({ label: lbl, color: this.categoryDonutPalette[i]||this.p.fallback }));
              const tc = this.p.categorical;
              this.smallTransactionDonutData = { labels: ll, datasets: [{ data: lv, backgroundColor: tc.slice(0, ll.length), hoverOffset: 4 }] };
            }

            // d[6]: Top 5 Level4
            const t5 = d[6] || [];
            {
              const tl = t5.map((r:any) => r.X_Level4||'Unknown');
              const tv2 = t5.map((r:any) => r.Y_Total_Transaction_Amount||0);
              this.topLevel4 = t5;
              this.topCategoriesBarData = { labels: tl, datasets: [{ label: 'Spend', data: tv2, backgroundColor: this.topCategoriesPalette.slice(0, tl.length) }] };
              this.topLegendItems = tl.map((lbl:string, i:number) => ({ label: lbl, color: this.topCategoriesPalette[i]||this.p.brandDark }));
            }

            // d[7]: Yearly Spend
            const ya = d[7] || [];
            if (ya.length) { this.yearlyBarData = { labels: ya.map((r:any) => String(r.Year)), datasets: [{ label: 'Total Spend', data: ya.map((r:any) => r.Total_Spend||0), backgroundColor: this.p.brandDark }] }; }

            // d[8]: Recommendations
            this.spendRecommendations = d[8] || [];

            // d[9]: HeadOfTail, d[10]: TailOfTail
            if (d[9]?.length) { const hv = d[9][0]?.HeadOfTailSpend; this.headOfTailTotal = (hv!==''&&hv!=null) ? Number(hv)||0 : 0; }
            if (d[10]?.length) this.tailOfTailTotal = Number(d[10][0]?.TailOfTailSpend)||0;

          } catch (err) { console.error('Error processing TailSpendLevel1 donut drill', err); }
        },
        error: err => console.error('TailSpendLevel1 Donut Drill API error', err)
      });
  }

  onHeadOfTailClick(event: any) {
    const active = event?.active;
    // If no slice clicked or "Rest" segment (index 1) clicked -> reset
    if (!active || !active.length || active[0].index !== 0) {
      this.clearFilter();
      return;
    }
    // Clicked "Head" segment: toggle or drill into top head-of-tail supplier
    if (this.selectedHeadOfTailSupplier) {
      this.clearFilter();
      return;
    }
    const topSupplier = this.headOfTailTopSuppliers?.[0]?.name;
    if (!topSupplier) { return; }
    this.onHeadOfTailSupplierClick(topSupplier);
  }

  onTailOfTailClick(event: any) {
    const active = event?.active;
    if (!active || !active.length || active[0].index !== 0) { this.clearFilter(); return; }
    if (this.selectedTailOfTailSupplier) { this.clearFilter(); return; }
    const topSupplier = this.tailOfTailTopSuppliers?.[0]?.name;
    if (!topSupplier) { return; }
    this.onTailOfTailSupplierClick(topSupplier);
  }

  onYearlyBarClick(event: any) {
    const active = event?.active;
    if (!active || !active.length) {
      this.clearFilter();
      return;
    }
    const idx = active[0].index;
    const label = (this.yearlyBarData.labels as string[])[idx];
    const vals = (this.yearlyBarData.datasets?.[0]?.data as number[]) || [];
    const isCtrlY = !!(event?.event?.native?.ctrlKey || event?.event?.native?.metaKey);
    if (this.selectedYear === null && this.handleMultiSelectToggle('Year', label, isCtrlY)) return;

    // If already in monthly view (year selected), do month drill
    if (this.selectedYear !== null) {
      this.drillByMonth(label, idx, vals);
      return;
    }

    // Year view: skip zero-spend bars
    if (!vals[idx]) {
      return;
    }

    // Toggle: clicking the same year resets
    if (this.selectedYear === label) {
      this.clearFilter();
      return;
    }

    if (!this.originalState) {
      this.backupInitialState();
    }

    this.selectedYear = label;
    this.selectedMonth = null;
    this.selectedLegend = null;
    this.selectedTopLegend = null;

    // Highlight selected year bar, dim others
    try {
      const labels = (this.yearlyBarData.labels as string[]) || [];
      const bg = labels.map(l => l === label ? this.p.brandDark : this.p.dim);
      this.yearlyBarData = { ...this.yearlyBarData, datasets: [{ ...this.yearlyBarData.datasets[0], backgroundColor: bg }] };
    } catch (e) { }

    this.spendIntelligenceService
      .getSpendIntelligenceDrillByYear({ YearNumber: label })
      .subscribe({
        next: (response: any) => {
          this.applyYearDrillResponse(response?.data || []);
        },
        error: err => console.error('Year Drill API error', err)
      });
  }

  drillByMonth(monthName: string, idx: number, vals: number[]) {
    // Skip zero-spend months
    if (!vals[idx]) return;

    // Toggle: clicking the same month restores the full monthly view
    if (this.selectedMonth === monthName) {
      this.selectedMonth = null;
      this.spendIntelligenceService
        .getSpendIntelligenceDrillByYear({ YearNumber: this.selectedYear })
        .subscribe({
          next: (response: any) => { this.applyYearDrillResponse(response?.data || []); },
          error: err => console.error('Year Drill API error', err)
        });
      return;
    }

    this.selectedMonth = monthName;

    // Highlight selected month bar, dim others
    try {
      const labels = (this.yearlyBarData.labels as string[]) || [];
      const bg = labels.map(l => l === monthName ? this.p.brandDark : this.p.dim);
      this.yearlyBarData = { ...this.yearlyBarData, datasets: [{ ...this.yearlyBarData.datasets[0], backgroundColor: bg }] };
    } catch (e) { }

    // Look up Month_Number from stored monthlyData
    const monthRow = this.monthlyData.find((r: any) => (r.Period_Label || String(r.Period_Number)) === monthName);
    if (!monthRow) return;

    this.spendIntelligenceService
      .getSpendIntelligenceDrillByYear({ YearNumber: this.selectedYear, MonthNumber: monthRow.Period_Number })
      .subscribe({
        next: (response: any) => {
          this.applyYearDrillResponse(response?.data || [], monthName);
        },
        error: err => console.error('Month Drill API error', err)
      });
  }

  // Multi-select: toggle an item into the multi-selection list
  // Returns true if this was a Ctrl+click (caller should not proceed with single-select)
  handleMultiSelectToggle(type: string, label: string, isCtrl: boolean): boolean {
    if (!isCtrl) {
      // Normal click: clear any existing multi-select and proceed with single-select
      this.multiSelectedItems = [];
      return false;
    }
    // Ctrl+click: toggle this item
    if (!this.originalState) { this.backupInitialState(); }
    const existingIdx = this.multiSelectedItems.findIndex(x => x.type === type && x.label === label);
    if (existingIdx >= 0) {
      this.multiSelectedItems.splice(existingIdx, 1);
    } else {
      // If a different type is already selected, start fresh with the new type
      if (this.multiSelectedItems.length > 0 && this.multiSelectedItems[0].type !== type) {
        this.multiSelectedItems = [];
      }
      this.multiSelectedItems.push({ type, label });
    }
    if (this.multiSelectedItems.length === 0) {
      this.clearFilter();
    } else {
      this.updateMultiSelectHighlights();
      this.performMultiSelectDrill();
    }
    return true;
  }

  // Apply immediate visual highlighting so user sees which items are selected
  updateMultiSelectHighlights() {
    const type = this.multiSelectedItems[0]?.type;
    const selected = new Set(this.multiSelectedItems.map(x => x.label));
    try {
      if (type === 'Level4') {
        const labels = (this.topCategoriesBarData.labels as string[]) || [];
        const bg = labels.map((l, i) => selected.has(l)
          ? this.topCategoriesPalette[i % this.topCategoriesPalette.length]
          : this.p.dim);
        this.topCategoriesBarData = { ...this.topCategoriesBarData, datasets: [{ ...this.topCategoriesBarData.datasets[0], backgroundColor: bg }] };
      } else if (type === 'Level1') {
        const labels = (this.categoryDonutData.labels as string[]) || [];
        const bg = labels.map((l, i) => selected.has(l)
          ? this.categoryDonutPalette[i % this.categoryDonutPalette.length]
          : this.p.dim);
        this.categoryDonutData = { ...this.categoryDonutData, datasets: [{ ...this.categoryDonutData.datasets[0], backgroundColor: bg }] };
      } else if (type === 'Year') {
        const labels = (this.yearlyBarData.labels as string[]) || [];
        const bg = labels.map(l => selected.has(l) ? this.p.brandDark : this.p.dim);
        this.yearlyBarData = { ...this.yearlyBarData, datasets: [{ ...this.yearlyBarData.datasets[0], backgroundColor: bg }] };
      } else if (type === 'TailSpendLevel1') {
        const labels = (this.smallDistributionBarData.labels as string[]) || [];
        const bg = labels.map(l => selected.has(l) ? this.p.accent : this.p.dim);
        this.smallDistributionBarData = { ...this.smallDistributionBarData, datasets: [{ ...this.smallDistributionBarData.datasets[0], backgroundColor: bg }] };
      }
      // 'Supplier' type (Recommendations) — highlighting is handled via template binding
    } catch (e) { }
  }

  isRecommendationMultiSelected(category: string): boolean {
    return this.multiSelectedItems.some(x => x.type === 'Supplier' && x.label === category);
  }

  performMultiSelectDrill() {
    if (!this.multiSelectedItems.length) return;
    const selectionType = this.multiSelectedItems[0].type;
    const selectionValues = this.multiSelectedItems.map(x => x.label).join(',');
    // Clear single-select states
    this.selectedLegend = null;
    this.selectedTopLegend = null;
    this.selectedYear = null;
    this.selectedMonth = null;
    this.selectedRecommendation = null;
    this.selectedDistribution = null;
    this.selectedTransactionDonut = null;
    this.selectedHeadOfTailSupplier = null;
    this.selectedTailOfTailSupplier = null;
    // Snapshot the source chart so we can restore it after the API response
    // (the chart you clicked should stay highlighted, not be replaced by drill data)
    const savedTopCategoriesBarData = selectionType === 'Level4'
      ? JSON.parse(JSON.stringify(this.topCategoriesBarData)) : null;
    const savedTopLevel4 = selectionType === 'Level4'
      ? JSON.parse(JSON.stringify(this.topLevel4)) : null;
    const savedCategoryDonutData = selectionType === 'Level1'
      ? JSON.parse(JSON.stringify(this.categoryDonutData)) : null;
    const savedYearlyBarData = selectionType === 'Year'
      ? JSON.parse(JSON.stringify(this.yearlyBarData)) : null;
    const savedDistBarData = selectionType === 'TailSpendLevel1'
      ? JSON.parse(JSON.stringify(this.smallDistributionBarData)) : null;
    const savedSpendRecommendations = selectionType === 'Supplier'
      ? JSON.parse(JSON.stringify(this.spendRecommendations)) : null;
    this.spendIntelligenceService
      .getSpendIntelligenceDrillByMultiSelect({ SelectionType: selectionType, SelectionValues: selectionValues })
      .subscribe({
        next: (response: any) => {
          this.applyYearDrillResponse(response?.data || []);
          // Restore the source chart (keep original with highlights, don't replace it)
          if (savedTopCategoriesBarData) this.topCategoriesBarData = savedTopCategoriesBarData;
          if (savedTopLevel4) this.topLevel4 = savedTopLevel4;
          if (savedCategoryDonutData) this.categoryDonutData = savedCategoryDonutData;
          if (savedYearlyBarData) this.yearlyBarData = savedYearlyBarData;
          if (savedDistBarData) this.smallDistributionBarData = savedDistBarData;
          if (savedSpendRecommendations) this.spendRecommendations = savedSpendRecommendations;
          // Re-apply highlights on the source chart after restore
          this.updateMultiSelectHighlights();
        },
        error: err => console.error('MultiSelect Drill API error', err)
      });
  }

  applyYearDrillResponse(d: any[], highlightMonth?: string) {
    try {
      // d[0]: Metadata (Selected_Year, Drill_Level) – skip

      // d[1]: Total Category Spend + Tail Spend Total
      const totalCatVal = d[1]?.[0]?.Total_Category_Spend ?? null;
      if (totalCatVal !== null) this.totalCategorySpend = Number(totalCatVal);
      const tailSpendVal = d[1]?.[0]?.Tail_Spend_Total ?? null;
      if (tailSpendVal !== null) this.tailSpendTotal = Number(tailSpendVal);

      // d[2]: ABC Summary -> always update both head/tail donuts
      const abcArr = d[2] || [];
      this.abcSummary = abcArr;
      {
        const bRow = abcArr.find((r: any) => (r.ABC_Class || '').toUpperCase() === 'B');
        const cRow = abcArr.find((r: any) => (r.ABC_Class || '').toUpperCase() === 'C');
        const bPct = bRow ? Number(bRow.Percentage ?? 0) : 0;
        this.headOfTailPercentage = bPct;
        const headPct = Math.min(Math.max(bPct, 0), 100);
        this.headOfTailDonutData = { labels: ['Head', 'Rest'], datasets: [{ data: [headPct, 100 - headPct], backgroundColor: [this.p.head, this.p.dim], hoverOffset: 0 }] };
        const cPct = cRow ? Number(cRow.Percentage ?? 0) : 0;
        this.tailOfTailPercentage = cPct;
        const tailPct = Math.min(Math.max(cPct, 0), 100);
        this.tailOfTailDonutData = { labels: ['Tail', 'Rest'], datasets: [{ data: [tailPct, 100 - tailPct], backgroundColor: [this.p.tail, this.p.dim], hoverOffset: 0 }] };
      }

      // d[3]: Suppliers by ABC_Class B/C
      const suppArr = d[3] || [];
      this.tailSuppliers = suppArr;
      this.headOfTailTopSuppliers = suppArr
        .filter((s: any) => s?.ABC_Class === 'B')
        .map((s: any) => ({ name: s.Normalised_Supplier_Name, amount: Number(s.Supplier_Total_Spend || s.Transaction_Amount) || 0 }));
      this.tailOfTailTopSuppliers = suppArr
        .filter((s: any) => s?.ABC_Class === 'C')
        .map((s: any) => ({ name: s.Normalised_Supplier_Name, amount: Number(s.Supplier_Total_Spend || s.Transaction_Amount) || 0 }));

      // d[4]: Level1 Category Breakdown -> redraw category donut
      const level1Arr = d[4] || [];
      if (level1Arr.length) {
        const labels = level1Arr.map((r: any) => r.Level1 || 'Unknown');
        const values = level1Arr.map((r: any) => r.Total_Transaction_Amount || 0);
        this.categoryDonutData = {
          labels,
          datasets: [{ data: values, backgroundColor: this.categoryDonutPalette.slice(0, labels.length), hoverOffset: 6 }],
        };
        this.legendItems = labels.map((lbl: string, i: number) => ({ label: lbl, color: this.categoryDonutPalette[i] || this.p.fallback }));
        const tColorsY = this.p.categorical;
        this.smallTransactionDonutData = { labels, datasets: [{ data: values, backgroundColor: tColorsY.slice(0, labels.length), hoverOffset: 4 }] };
      }

      // d[5]: Period breakdown (months with spend) -> rebuild yearly bar
      // Uses Period_Label (e.g. 'Jan') and Period_Number for month drill lookup
      const periodArr = d[5] || [];
      if (periodArr.length) {
        this.monthlyData = periodArr;
        this.yearlyBarData = {
          labels: periodArr.map((r: any) => r.Period_Label || String(r.Period_Number)),
          datasets: [{ label: 'Monthly Spend', data: periodArr.map((r: any) => r.Total_Spend || 0),
            backgroundColor: periodArr.map((r: any) => {
              const lbl = r.Period_Label || String(r.Period_Number);
              if (highlightMonth) return lbl === highlightMonth ? this.p.brandDark : this.p.dim;
              return (r.Total_Spend || 0) > 0 ? this.p.brandDark : this.p.dim;
            })
          }],
        };
      }

      // d[6]: Quarterly breakdown (skip)

      // d[7]: Top 5 Level4 categories
      const top5Arr = d[7] || [];
      {
        const topLabels = top5Arr.map((r: any) => r.X_Level4 || 'Unknown');
        const topValues = top5Arr.map((r: any) => r.Y_Total_Transaction_Amount || 0);
        this.topLevel4 = top5Arr;
        this.topCategoriesBarData = {
          labels: topLabels,
          datasets: [{ label: 'Spend', data: topValues, backgroundColor: this.topCategoriesPalette.slice(0, topLabels.length) }],
        };
        this.topLegendItems = topLabels.map((lbl: string, i: number) => ({ label: lbl, color: this.topCategoriesPalette[i] || this.p.brandDark }));
      }

      // d[8]: Spend Recommendations
      this.spendRecommendations = d[8] || [];

      // d[9]: Level1 tail distribution
      const distArr = d[9] || [];
      if (distArr.length) {
        this.smallDistributionBarData = {
          labels: distArr.map((r: any) => r.Level1 || 'Unknown'),
          datasets: [{ label: 'Share', data: distArr.map((r: any) => r.Tail_Spend_Percentage ?? 0), backgroundColor: this.p.accent, borderRadius: 6, barPercentage: 0.6 }],
        };
      }

      // d[10]: Head of Tail total
      if (d[10]?.length) { const hVal = d[10][0]?.HeadOfTailSpend; this.headOfTailTotal = (hVal !== '' && hVal != null) ? Number(hVal) || 0 : 0; }

      // d[11]: Tail of Tail total
      if (d[11]?.length) this.tailOfTailTotal = Number(d[11][0]?.TailOfTailSpend) || 0;

    } catch (err) {
    }
  }

  onKpiClick(kpi: string) {
    // For now KPI clicks reset filters when clicked, or clear supplier selection
    if (this.selectedLegend || this.selectedSupplier) {
      this.clearFilter();
    } else {
      // no-op or future expansion (could drill into KPI)
      this.clearFilter();
    }
  }

  onSupplierClick(name: string) {
    if (this.selectedSupplier === name) {
      this.selectedSupplier = null;
    } else {
      this.selectedSupplier = name;
      this.selectedLegend = null;
    }
  }

  onHeadOfTailSupplierClick(name: string, event?: MouseEvent) {
    const isCtrl = !!(event?.ctrlKey || event?.metaKey);
    if (this.handleMultiSelectToggle('HeadOfTailSupplier', name, isCtrl)) return;
    if (this.selectedHeadOfTailSupplier === name) { this.clearFilter(); return; }
    if (!this.originalState) { this.backupInitialState(); }
    this.selectedHeadOfTailSupplier = name;
    this.selectedSupplier = null;
    this.selectedLegend = null;
    this.selectedTopLegend = null;
    this.selectedYear = null;
    this.selectedMonth = null;
    this.selectedRecommendation = null;
    this.selectedDistribution = null;
    this.selectedTransactionDonut = null;
    this.spendIntelligenceService
      .getSpendIntelligenceDrillByHeadOfTailSupplier({ SupplierName: name })
      .subscribe({
        next: (response: any) => {
          try {
            const d = response?.data || [];

            // d[0]: Metadata (Selected_Supplier, Drill_Level) – skip

            // d[1]: Supplier KPIs
            if (d[1]?.[0]) {
              const k = d[1][0];
              this.totalCategorySpend = Number(k.Total_Spend || 0);
            }

            // d[2]: Level1/2/3/4 taxonomy breakdown for this supplier
            const taxArr = d[2] || [];
            if (taxArr.length) {
              // Top Categories bar – use Level4 as labels
              const topLabels = taxArr.map((r: any) => r.Level4 || r.Level3 || 'Unknown');
              const topValues = taxArr.map((r: any) => Number(r.Total_Transaction_Amount || 0));
              this.topCategoriesBarData = {
                labels: topLabels,
                datasets: [{ label: 'Spend', data: topValues, backgroundColor: this.topCategoriesPalette.slice(0, topLabels.length) }],
              };
              this.topLevel4 = taxArr.map((r: any) => ({ X_Level4: r.Level4 || r.Level3, Y_Total_Transaction_Amount: Number(r.Total_Transaction_Amount || 0), Bubble_Distinct_Parent_Count: 1 }));
              this.topLegendItems = topLabels.map((lbl: string, i: number) => ({ label: lbl, color: this.topCategoriesPalette[i] || this.p.brandDark }));

              // Category donut – group by Level2
              const level2Map: { [k: string]: number } = {};
              taxArr.forEach((r: any) => {
                const l2 = r.Level2 || 'Unknown';
                level2Map[l2] = (level2Map[l2] || 0) + Number(r.Total_Transaction_Amount || 0);
              });
              const l2Labels = Object.keys(level2Map);
              const l2Values = l2Labels.map(k => level2Map[k]);
              this.categoryDonutData = {
                labels: l2Labels,
                datasets: [{ data: l2Values, backgroundColor: this.categoryDonutPalette.slice(0, l2Labels.length), hoverOffset: 6 }],
              };
              this.legendItems = l2Labels.map((lbl: string, i: number) => ({ label: lbl, color: this.categoryDonutPalette[i] || this.p.fallback }));
              const tc = this.p.categorical;
              this.smallTransactionDonutData = { labels: l2Labels, datasets: [{ data: l2Values, backgroundColor: tc.slice(0, l2Labels.length), hoverOffset: 4 }] };
            }

            // d[3]: Yearly Spend
            const yearlyArr = d[3] || [];
            if (yearlyArr.length) {
              this.yearlyBarData = {
                labels: yearlyArr.map((r: any) => String(r.Year)),
                datasets: [{ label: 'Total Spend', data: yearlyArr.map((r: any) => r.Total_Spend || 0), backgroundColor: this.p.brandDark }],
              };
            }

            // d[4]: Year×Month matrix – skip (no dedicated monthly visual for supplier drill)

          } catch (err) {
          }
        },
        error: err => console.error('HeadOfTailSupplier Drill API error', err)
      });
  }

  onTailOfTailSupplierClick(name: string, event?: MouseEvent) {
    const isCtrl = !!(event?.ctrlKey || event?.metaKey);
    if (this.handleMultiSelectToggle('TailOfTailSupplier', name, isCtrl)) return;
    if (this.selectedTailOfTailSupplier === name) { this.clearFilter(); return; }
    if (!this.originalState) { this.backupInitialState(); }
    this.selectedTailOfTailSupplier = name;
    this.selectedHeadOfTailSupplier = null;
    this.selectedSupplier = null;
    this.selectedLegend = null;
    this.selectedTopLegend = null;
    this.selectedYear = null;
    this.selectedMonth = null;
    this.selectedRecommendation = null;
    this.selectedDistribution = null;
    this.selectedTransactionDonut = null;
    this.spendIntelligenceService
      .getSpendIntelligenceDrillByHeadOfTailSupplier({ SupplierName: name })
      .subscribe({
        next: (response: any) => {
          try {
            const d = response?.data || [];
            if (d[1]?.[0]) { this.totalCategorySpend = Number(d[1][0].Total_Spend || 0); }
            const taxArr = d[2] || [];
            if (taxArr.length) {
              const topLabels = taxArr.map((r: any) => r.Level4 || r.Level3 || 'Unknown');
              const topValues = taxArr.map((r: any) => Number(r.Total_Transaction_Amount || 0));
              this.topCategoriesBarData = { labels: topLabels, datasets: [{ label: 'Spend', data: topValues, backgroundColor: this.topCategoriesPalette.slice(0, topLabels.length) }] };
              this.topLevel4 = taxArr.map((r: any) => ({ X_Level4: r.Level4 || r.Level3, Y_Total_Transaction_Amount: Number(r.Total_Transaction_Amount || 0), Bubble_Distinct_Parent_Count: 1 }));
              this.topLegendItems = topLabels.map((lbl: string, i: number) => ({ label: lbl, color: this.topCategoriesPalette[i] || this.p.brandDark }));
              const l2Map: { [k: string]: number } = {};
              taxArr.forEach((r: any) => { const l2 = r.Level2 || 'Unknown'; l2Map[l2] = (l2Map[l2] || 0) + Number(r.Total_Transaction_Amount || 0); });
              const l2Labels = Object.keys(l2Map); const l2Values = l2Labels.map((k: string) => l2Map[k]);
              this.categoryDonutData = { labels: l2Labels, datasets: [{ data: l2Values, backgroundColor: this.categoryDonutPalette.slice(0, l2Labels.length), hoverOffset: 6 }] };
              this.legendItems = l2Labels.map((lbl: string, i: number) => ({ label: lbl, color: this.categoryDonutPalette[i] || this.p.fallback }));
              const tc = this.p.categorical;
              this.smallTransactionDonutData = { labels: l2Labels, datasets: [{ data: l2Values, backgroundColor: tc.slice(0, l2Labels.length), hoverOffset: 4 }] };
            }
            const ya = d[3] || [];
            if (ya.length) { this.yearlyBarData = { labels: ya.map((r: any) => String(r.Year)), datasets: [{ label: 'Total Spend', data: ya.map((r: any) => r.Total_Spend || 0), backgroundColor: this.p.brandDark }] }; }
          } catch (err) {
          }
        },
        error: err => console.error('TailOfTailSupplier Drill API error', err)
      });
  }

  applyCategoryFilter(label: string | null) {
    this.selectedLegend = label;

    // Category donut: highlight selected slice, fade others
    try {
      const labels = (this.categoryDonutData.labels as string[]) || [];
      const bg = labels.map(l => (l === label ? this.categoryDonutPalette[labels.indexOf(l) % this.categoryDonutPalette.length] : this.p.dim));
      if (this.categoryDonutData.datasets && this.categoryDonutData.datasets[0]) {
        this.categoryDonutData = { ...this.categoryDonutData, datasets: [{ ...this.categoryDonutData.datasets[0], backgroundColor: bg }] };
      }
    } catch (e) { 
    }

    // Top categories bar: dim bars that don't match
    try {
      const labels = (this.topCategoriesBarData.labels as string[]) || [];
      const bg = labels.map(l => (l === label ? this.topCategoriesPalette[labels.indexOf(l) % this.topCategoriesPalette.length] : this.p.dim));
      if (this.topCategoriesBarData.datasets && this.topCategoriesBarData.datasets[0]) {
        this.topCategoriesBarData = { ...this.topCategoriesBarData, datasets: [{ ...this.topCategoriesBarData.datasets[0], backgroundColor: bg }] };
      }
    } catch (e) { 
    }

    // Small transaction donut: highlight matching label if present
    try {
      const labels = (this.smallTransactionDonutData.labels as string[]) || [];
      const bg = labels.map(l => (l === label ? this.smallTransactionPalette[labels.indexOf(l) % this.smallTransactionPalette.length] : this.p.dim));
      if (this.smallTransactionDonutData.datasets && this.smallTransactionDonutData.datasets[0]) {
        this.smallTransactionDonutData = { ...this.smallTransactionDonutData, datasets: [{ ...this.smallTransactionDonutData.datasets[0], backgroundColor: bg }] };
      }
    } catch (e) { 
    }

    // Update KPI values (Total Category Spend and Identified Tail Spend) when a category is selected
    try {
      let categorySpend: number | null = null;

      // 1) try Top 5 data (topLevel4 array)
      if (label && Array.isArray(this.topLevel4) && this.topLevel4.length) {
        const found = this.topLevel4.find(t => (t.X_Level4 || '').toString() === label.toString());
        if (found && found.Y_Total_Transaction_Amount != null) {
          categorySpend = Number(found.Y_Total_Transaction_Amount) || 0;
        }
      }

      // 2) try topCategoriesBarData
      if (categorySpend == null && label && this.topCategoriesBarData && this.topCategoriesBarData.labels) {
        const labels = (this.topCategoriesBarData.labels as string[]) || [];
        const idx = labels.indexOf(label as string);
        const vals = (this.topCategoriesBarData.datasets && this.topCategoriesBarData.datasets[0] && (this.topCategoriesBarData.datasets[0].data as any[])) || [];
        if (idx >= 0 && vals[idx] != null) {
          categorySpend = Number(vals[idx]) || 0;
        }
      }

      // 3) try categoryDonutData
      if (categorySpend == null && label && this.categoryDonutData && this.categoryDonutData.labels) {
        const labels = (this.categoryDonutData.labels as string[]) || [];
        const idx = labels.indexOf(label as string);
        const vals = (this.categoryDonutData.datasets && this.categoryDonutData.datasets[0] && (this.categoryDonutData.datasets[0].data as any[])) || [];
        if (idx >= 0 && vals[idx] != null) {
          categorySpend = Number(vals[idx]) || 0;
        }
      }

      // fallback to overall total
      if (categorySpend == null && this.overallTotalCategorySpend != null) {
        categorySpend = this.overallTotalCategorySpend;
      }

      if (categorySpend != null) this.totalCategorySpend = categorySpend;

      // Determine tail spend for the selected category
      let categoryTail: number | null = null;

      // 1) try smallTransactionDonutData (actual amounts per label)
      if (label && this.smallTransactionDonutData && this.smallTransactionDonutData.labels) {
        const labels = (this.smallTransactionDonutData.labels as string[]) || [];
        const idx = labels.indexOf(label as string);
        const vals = (this.smallTransactionDonutData.datasets && this.smallTransactionDonutData.datasets[0] && (this.smallTransactionDonutData.datasets[0].data as any[])) || [];
        if (idx >= 0 && vals[idx] != null) {
          categoryTail = Number(vals[idx]) || 0;
        }
      }

      // 2) try smallDistributionBarData (percentage) * overallTailSpendTotal
      if (categoryTail == null && label && this.smallDistributionBarData && this.smallDistributionBarData.labels) {
        const labels = (this.smallDistributionBarData.labels as string[]) || [];
        const idx = labels.indexOf(label as string);
        const vals = (this.smallDistributionBarData.datasets && this.smallDistributionBarData.datasets[0] && (this.smallDistributionBarData.datasets[0].data as any[])) || [];
        if (idx >= 0 && vals[idx] != null && this.overallTailSpendTotal != null) {
          // smallDistribution values are percentages (e.g., 8 => 8%). If they are decimals, this still works.
          const pct = Number(vals[idx]) || 0;
          categoryTail = (Number(this.overallTailSpendTotal) * (pct / 100));
        }
      }

      // fallback to overall tail total
      if (categoryTail == null && this.overallTailSpendTotal != null) {
        categoryTail = this.overallTailSpendTotal;
      }

      if (categoryTail != null) this.tailSpendTotal = categoryTail;
    } catch (e) { 
    }
  }

  clearFilter() {
  this.selectedLegend = null;
  this.selectedTopLegend = null;
  this.selectedSupplier = null;
  this.selectedYear = null;
  this.selectedMonth = null;
  this.selectedRecommendation = null;
  this.selectedDistribution = null;
  this.selectedTransactionDonut = null;
  this.selectedHeadOfTailSupplier = null;
  this.selectedTailOfTailSupplier = null;
  this.monthlyData = [];
  this.multiSelectedItems = [];

  if (!this.originalState) return;

  const s = structuredClone(this.originalState);

  // Restore KPIs
  this.totalCategorySpend = s.totalCategorySpend;
  this.tailSpendTotal = s.tailSpendTotal;
  this.overallTotalCategorySpend = s.overallTotalCategorySpend;
  this.overallTailSpendTotal = s.overallTailSpendTotal;

  // Restore charts
  this.categoryDonutData = s.categoryDonutData;
  this.topCategoriesBarData = s.topCategoriesBarData;
  this.smallTransactionDonutData = s.smallTransactionDonutData;
  this.smallDistributionBarData = s.smallDistributionBarData;
  this.yearlyBarData = s.yearlyBarData;

  // Restore 9 result sets
  this.abcSummary = s.abcSummary;
  this.tailSuppliers = s.tailSuppliers;
  this.spendRecommendations = s.spendRecommendations;

  this.headOfTailTotal = s.headOfTailTotal;
  this.headOfTailPercentage = s.headOfTailPercentage;
  this.headOfTailTopSuppliers = s.headOfTailTopSuppliers;
  if (s.headOfTailDonutData) this.headOfTailDonutData = s.headOfTailDonutData;

  this.tailOfTailTotal = s.tailOfTailTotal;
  this.tailOfTailPercentage = s.tailOfTailPercentage;
  this.tailOfTailTopSuppliers = s.tailOfTailTopSuppliers;
  if (s.tailOfTailDonutData) this.tailOfTailDonutData = s.tailOfTailDonutData;

  // Restore Level1
  this.level1CategoryLabel = s.level1CategoryLabel;
  this.level1TotalTransactionAmount = s.level1TotalTransactionAmount;

  // Restore legends
  this.legendItems = s.legendItems;
  this.topLegendItems = s.topLegendItems;

  // Restore Top 5 Categories bar bubbles
  this.topLevel4 = s.topLevel4 ?? [];
}

  // Doughnut chart for category breakdown (Spend Intelligence).
  // backgroundColor is a placeholder — `recolorActiveCharts()` overwrites it
  // synchronously with the active theme palette during the constructor.
  categoryDonutData: ChartConfiguration<'doughnut'>['data'] = {
    labels: ['Consulting', 'IT & Software', 'Logistics', 'Office', 'Other'],
    datasets: [
      {
        data: [28, 34, 18, 12, 8],
        backgroundColor: ['#0ea5a4', '#3aa3ff', '#7c6dfa', '#f97316', '#ff5e7e'],
        hoverOffset: 6,
      },
    ],
  };

  categoryDonutOptions: ChartConfiguration<'doughnut'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
  };

  // Bar chart for Top 5 Categories (Spend Intelligence).
  // backgroundColor is a placeholder — `recolorActiveCharts()` overwrites it
  // synchronously with the active theme palette during the constructor.
  topCategoriesBarData: ChartConfiguration<'bar'>['data'] = {
    labels: ['Cloud Services', 'Software Licenses', 'Travel & Expense', 'Office Supplies', 'Consulting'],
    datasets: [
      {
        label: 'Spend',
        data: [450, 900, 350, 2100, 420],
        backgroundColor: '#0b2f5a',
      },
    ],
  };

  topCategoriesBarOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true },
    },
    plugins: {
      legend: { display: false },
    },
  };

  // plugin to draw count bubbles above bars
  topBubblePlugin = {
    id: 'topBubblePlugin',
    afterDatasetsDraw: (chart: any) => {
      if (!this.topLevel4 || !this.topLevel4.length) return;
      const ctx = chart.ctx;
      const meta = chart.getDatasetMeta(0);
      meta.data.forEach((bar: any, index: number) => {
        const count = this.topLevel4[index] ? this.topLevel4[index].Bubble_Distinct_Parent_Count : null;
        if (count == null) return;
        const x = bar.x;
        const y = bar.y - 8; // position above bar

        // draw bubble
        ctx.save();
        ctx.beginPath();
        ctx.fillStyle = this.p.accent;
        ctx.strokeStyle = this.p.textOnBrand;
        ctx.lineWidth = 2;
        ctx.arc(x, y - 12, 14, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // draw count text
        ctx.fillStyle = this.p.textOnBrand;
        ctx.font = '12px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(count), x, y - 12);
        ctx.restore();
      });
    },
  };

  // Compact bar/donut for the Spend Distribution card. Seeded in
  // `seedDefaultCharts()` so all colors come from the active theme palette.
  smallDistributionBarData!: ChartConfiguration<'bar'>['data'];
  smallDistributionBarOptions!: ChartConfiguration<'bar'>['options'];
  smallTransactionDonutData!: ChartConfiguration<'doughnut'>['data'];

  smallTransactionDonutOptions: ChartConfiguration<'doughnut'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    cutout: '70%',
  };

}
